import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { withoutBidiControls as plain } from "../snippets/bidi";
import { httpHost } from "../snippets/linkHost";
import UntrustedMarkdown from "./UntrustedMarkdown";
import { formatDay, formatScore, noteAnchor, scoreTone, taskBoardPath, whoName } from "./lib/format";
import { proposalLine } from "./lib/verdictForm";

const SOURCE_KINDS = { filing: "Filing", metric: "Metric", url: "Web" };

/**
 * Where a piece of evidence is from. A web source is a link whose text IS
 * its host (D8: the host visible), read left to right in its own isolate
 * and opened without a referrer; anything that isn't a full http(s) URL
 * stays text. Filings and metrics are text. Bidi controls are stripped
 * (snippets/bidi.js).
 */
function EvidenceSource({ source }) {
  const kind = SOURCE_KINDS[source?.kind] ?? source?.kind ?? "Source";
  const ref = plain(source?.ref ?? "");
  const host = source?.kind === "url" ? httpHost(ref) : null;
  if (!host) {
    return (
      <span className="text-muted-foreground">
        {kind}: <bdi className="wrap-anywhere">{ref}</bdi>
      </span>
    );
  }
  return (
    <span className="text-muted-foreground">
      {kind}:{" "}
      <a
        href={ref}
        target="_blank"
        rel="noopener noreferrer nofollow"
        title={ref}
        className="wrap-anywhere text-primary underline underline-offset-2"
      >
        <bdi dir="ltr">{host}</bdi>{" "}
        <span className="sr-only">(opens in a new tab)</span>
      </a>
    </span>
  );
}

EvidenceSource.propTypes = {
  source: PropTypes.shape({ kind: PropTypes.string, ref: PropTypes.string }),
};

/**
 * One research note (spec §5.2): who wrote it, when and for which task;
 * what it proposed; its summary (agent-written markdown, rendered untrusted
 * — D8); its typed evidence with each web source's host; and the score it
 * gave each thesis, with the rationale. A retracted one says who retracted
 * it and why. `id="note-<id>"` is the anchor research tasks link to.
 *
 * `onRetract` (a person's only — the page passes it for a person alone)
 * opens the retract dialog for this note.
 */
function NoteCard({ note, thesisTitle, onRetract = null }) {
  const titleId = `${noteAnchor(note.id)}-title`;
  const day = formatDay(note.at);
  const evidence = Array.isArray(note.evidence) ? note.evidence : [];
  const scores = Array.isArray(note.scores) ? note.scores : [];
  const proposed = note.proposed_verdict
    ? { verdict: note.proposed_verdict, conviction: note.proposed_conviction }
    : null;
  return (
    <article
      id={noteAnchor(note.id)}
      aria-labelledby={titleId}
      tabIndex={-1}
      className={cn(
        "scroll-mt-20 rounded-lg border bg-card p-4 outline-none focus-visible:ring-[3px] focus-visible:ring-ring",
        note.retracted && "border-dashed",
      )}
    >
      <header className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 id={titleId} className="m-0 text-base font-semibold">
            Note #{note.id}
          </h3>
          <p className="m-0 text-sm text-muted-foreground">
            by {whoName(note.by)}
            {day ? ` on ${day}` : ""}
            {note.task && (
              <>
                {" · for "}
                <Link to={taskBoardPath(note.task.id)} className="underline underline-offset-2">
                  task #{note.task.id}
                </Link>
              </>
            )}
            {note.retracted && (
              <>
                {" "}
                <Badge variant="outline">Retracted</Badge>
              </>
            )}
          </p>
        </div>
        {onRetract && !note.retracted && (
          <Button
            id={`retract-note-${note.id}`}
            type="button"
            variant="outline"
            size="sm"
            className="h-11 md:h-8"
            onClick={() => onRetract(note.id)}
          >
            Retract{" "}
            <span className="sr-only">note #{note.id}</span>
          </Button>
        )}
      </header>

      {note.retracted && (
        <p className="mt-0 mb-3 text-sm">
          Retracted by {whoName(note.retracted_by)}
          {note.retracted_at ? ` on ${formatDay(note.retracted_at)}` : ""}: {note.retracted_reason}
        </p>
      )}

      {proposed && (
        <div className="mb-3 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
          <p className="m-0 font-medium">{proposalLine(proposed)}</p>
          {note.proposed_exit_condition && (
            <p className="m-0 wrap-anywhere">Exit condition: {note.proposed_exit_condition}</p>
          )}
          {note.proposed_primary_thesis && (
            <p className="m-0 wrap-anywhere">Primary thesis: {thesisTitle(note.proposed_primary_thesis)}</p>
          )}
        </div>
      )}

      <UntrustedMarkdown body={note.summary || ""} />

      {evidence.length > 0 && (
        <>
          <h4 id={`${titleId}-evidence`} className="mt-3 mb-1 text-sm font-semibold">
            Evidence ({evidence.length})
          </h4>
          <ul aria-label="Evidence" className="m-0 flex list-disc flex-col gap-1 ps-5 text-sm">
            {evidence.map((item, i) => (
              // Evidence has no id; its place in the note is its identity
              // (a note is append-only, so the list never reorders).
              // Label and value are each their own isolate: right-to-left
              // text in one can't reorder the source's host after it.
              <li key={i} className="wrap-anywhere">
                <span className="font-medium">
                  <bdi>{plain(item.label)}</bdi>:
                </span>{" "}
                <bdi>{plain(item.value)}</bdi>
                {" — "}
                <EvidenceSource source={item.source} />
              </li>
            ))}
          </ul>
        </>
      )}

      {scores.length > 0 && (
        <>
          <h4 className="mt-3 mb-1 text-sm font-semibold">Scores ({scores.length})</h4>
          <ul aria-label={`Scores in note #${note.id}`} className="m-0 flex list-none flex-col gap-1 p-0 text-sm">
            {scores.map((score) => (
              <li key={score.thesis} className="wrap-anywhere">
                <span className="font-medium">{thesisTitle(score.thesis)}</span>{" "}
                <span className={cn("font-semibold tabular-nums", scoreTone(score.score))}>
                  {formatScore(score.score)}
                </span>
                {score.rationale ? ` — ${plain(score.rationale)}` : ""}
              </li>
            ))}
          </ul>
        </>
      )}
    </article>
  );
}

NoteCard.propTypes = {
  note: PropTypes.shape({
    id: PropTypes.number.isRequired,
    summary: PropTypes.string,
    evidence: PropTypes.array,
    scores: PropTypes.array,
    proposed_verdict: PropTypes.string,
    proposed_conviction: PropTypes.number,
    proposed_exit_condition: PropTypes.string,
    proposed_primary_thesis: PropTypes.string,
    task: PropTypes.shape({ id: PropTypes.number.isRequired }),
    by: PropTypes.shape({ username: PropTypes.string }),
    at: PropTypes.string,
    retracted: PropTypes.bool,
    retracted_reason: PropTypes.string,
    retracted_by: PropTypes.shape({ username: PropTypes.string }),
    retracted_at: PropTypes.string,
  }).isRequired,
  /** A thesis's title from its slug (the slug when it isn't known). */
  thesisTitle: PropTypes.func.isRequired,
  /** `(noteId)`: open Retract — given only for a person. */
  onRetract: PropTypes.func,
};

export default NoteCard;
